/**
 * Locations Settings Page
 * Manage business locations, contact information and tax rates
 */

import { useState, useMemo } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { MapPin, Plus } from 'lucide-react';
import { SettingsPageHeader } from '@/components/ui';
import { BusinessSetupPageShell } from './business-setup/components/business-setup-page-shell';
import { LocationCard } from './business-setup/components/location-card';
import { RenameLocationModal } from './business-setup/modals/rename-location-modal';
import { DeleteLocationModal } from './business-setup/modals/delete-location-modal';
import { AddLocationModal } from './business-setup/modals/add-location-modal';
import { useGetLocations } from '@/api/generated/locations/locations';
import type { GetLocations200DataItem } from '@/api/generated/schemas';
import { formatDocumentTitle } from '@/lib/document-title';
import { useLocationTaxesShown } from './business-setup/hooks/use-location-taxes-shown';

export const Route = createFileRoute('/_auth/$orgId/settings/business-setup/locations')({
  component: LocationsPage,
});

function LocationsPage() {
  const { orgId } = useParams({ from: '/_auth/$orgId/settings/business-setup/locations' });

  // Modal states
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<GetLocations200DataItem | null>(null);
  const [renameRestoreFocusElement, setRenameRestoreFocusElement] = useState<HTMLElement | null>(
    null
  );

  // Editing state - tracks which location is being edited inline
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);

  // Fetch locations
  // Without a Hayven checkout there are no tax rates or gift cards to talk about.
  const showTaxes = useLocationTaxesShown();
  const { data: locationsResponse, isLoading, isError } = useGetLocations();
  const locations = useMemo(() => locationsResponse?.data ?? [], [locationsResponse?.data]);

  // Action handlers
  const handleEdit = (location: GetLocations200DataItem) => {
    setEditingLocationId(location.id);
  };

  const handleCancelEdit = () => {
    setEditingLocationId(null);
  };

  const handleRename = (
    location: GetLocations200DataItem,
    restoreFocusElement?: HTMLElement | null
  ) => {
    setSelectedLocation(location);
    setRenameRestoreFocusElement(restoreFocusElement ?? null);
    setRenameModalOpen(true);
  };

  const handleDelete = (location: GetLocations200DataItem) => {
    setSelectedLocation(location);
    setDeleteModalOpen(true);
  };

  const handleAddLocation = () => {
    setAddModalOpen(true);
  };

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Locations')}</title>
      </Helmet>

      <BusinessSetupPageShell orgId={orgId} activePath="locations">
        {/* Page Header */}
        <SettingsPageHeader
          title="Locations"
          description={
            showTaxes
              ? 'Manage your business locations, contact information and tax rates.'
              : 'Manage your business locations and contact information.'
          }
        />

        {/* Locations List */}
        <div className="max-w-2xl space-y-5">
          {isLoading ? (
            <div
              className="space-y-4 rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface p-6 shadow-soft"
              role="status"
              aria-label="Loading locations"
            >
              <div className="h-5 w-40 animate-pulse rounded-[var(--r-sm)] bg-clovie-surface-sunk" />
              <div className="grid gap-3 sm:grid-cols-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="space-y-2 rounded-[var(--r-xl)] bg-clovie-surface-2 p-3"
                  >
                    <div className="h-3 w-20 animate-pulse rounded-[var(--r-sm)] bg-clovie-surface-sunk" />
                    <div className="h-4 w-full animate-pulse rounded-[var(--r-sm)] bg-clovie-surface-sunk" />
                  </div>
                ))}
              </div>
              <span className="sr-only">Loading locations...</span>
            </div>
          ) : isError ? (
            <div className="rounded-[var(--r-2xl)] border border-[var(--rose-100)] bg-clovie-surface p-6 shadow-soft">
              <h2 className="text-[17px] font-semibold text-clovie-text">
                Locations could not load
              </h2>
              <p className="mt-1 text-sm leading-6 text-clovie-muted">
                Refresh the page or try again later. Existing location settings were not changed.
              </p>
            </div>
          ) : locations.length === 0 ? (
            <div className="rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface p-6 text-center shadow-soft sm:p-8">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-clovie-chip text-clovie-chip-fg">
                <MapPin className="h-5 w-5" />
              </div>
              <h2 className="text-[17px] font-semibold text-clovie-text">No locations yet</h2>
              <p className="mx-auto mt-2 max-w-sm text-[16px] text-clovie-muted">
                {showTaxes
                  ? 'Add your first location to manage address, contact, tax, and timezone details.'
                  : 'Add your first location to manage address, contact, and timezone details.'}
              </p>
            </div>
          ) : (
            locations.map((location) => (
              <LocationCard
                key={location.id}
                location={location}
                canDelete={locations.length > 1}
                isEditing={editingLocationId === location.id}
                onEdit={handleEdit}
                onCancelEdit={handleCancelEdit}
                onRename={handleRename}
                onDelete={handleDelete}
                showTaxes={showTaxes}
              />
            ))
          )}

          {/* Info text and Add button */}
          <div className="rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface p-5 shadow-soft">
            <p className="mb-4 text-[16px] leading-6 text-clovie-muted">
              {showTaxes
                ? 'Add locations to share some or all of your services, staff members, gift cards, and also have the option to run separate reports, set individual permissions, and more.'
                : 'Add locations to share some or all of your services and staff members, and also have the option to run separate reports, set individual permissions, and more.'}
            </p>
            <button
              type="button"
              onClick={handleAddLocation}
              disabled
              aria-disabled="true"
              title="Coming soon"
              className="flex min-h-11 cursor-not-allowed items-center gap-2 rounded-[var(--r-pill)] px-1 text-sm font-semibold text-clovie-brand-dark opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add a location
            </button>
            <p className="mt-2 text-sm leading-6 text-clovie-muted">
              Multi-location is not supported yet — coming soon.
            </p>
          </div>
        </div>
      </BusinessSetupPageShell>

      {/* Modals */}
      <RenameLocationModal
        open={renameModalOpen}
        onClose={() => setRenameModalOpen(false)}
        location={selectedLocation}
        restoreFocusElement={renameRestoreFocusElement}
      />

      <DeleteLocationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        location={selectedLocation}
      />

      <AddLocationModal open={addModalOpen} onClose={() => setAddModalOpen(false)} />
    </>
  );
}
